// Tenant CRUD and lookup for multi-tenant SaaS
import { ref, get, set, push, update, remove, query, orderByChild, equalTo } from 'firebase/database';
import { db } from './config';

const TENANTS_ROOT = 'tenants';

function deepMergeSiteContent(existing, next) {
  const out = { ...existing };
  Object.keys(next).forEach((key) => {
    if (next[key] && typeof next[key] === 'object' && !Array.isArray(next[key]) && key !== 'slots' && key !== 'items' && key !== 'list' && key !== 'addressLines' && key !== 'descriptionLines' && key !== 'categories' && key !== 'plans') {
      out[key] = deepMergeSiteContent(existing[key] || {}, next[key]);
    } else {
      out[key] = next[key];
    }
  });
  return out;
}

// Default branding/features for new tenants
export const DEFAULT_TENANT_BRANDING = {
  siteName: 'My Gym',
  logoUrl: '',
  primaryColor: '#1a1a2e',
  accentColor: '#e94560'
};

export const DEFAULT_TENANT_FEATURES = {
  contactFormEnabled: true,
  showClasses: true,
  showTrainers: true
};

// Default public website content (tenant admin can edit)
export const DEFAULT_SITE_CONTENT = {
  hero: {
    tagline: 'FIND YOUR ENERGY',
    titleLine1: 'MAKE YOUR BODY',
    titleLine2: 'FIT & PERFECT',
    descriptionLines: ['Transform Your Body Into A Powerhouse', 'Where Strength Meets Determination', 'Build The Ultimate Version Of Yourself']
  },
  timings: {
    gymType: '(UNISEX GYM)',
    subtitle: 'The Gym With The Motive',
    slots: [
      { period: '5:00 AM - 8:30 AM', type: 'UNISEX', typeClass: 'unisex' },
      { period: '8:30 AM - 11:30 AM', type: 'ONLY FEMALE', typeClass: 'female' },
      { period: '11:30 AM - 3:00 PM', type: 'UNISEX', typeClass: 'unisex' },
      { period: '3:00 PM - 5:15 PM', type: 'ONLY FEMALE', typeClass: 'female' },
      { period: '5:00 PM - 11:00 PM', type: 'UNISEX', typeClass: 'unisex' }
    ]
  },
  about: {
    tag: 'GYM FITNESS',
    title: 'Transform Your Body Into a Muscle Factory!',
    description: 'Our gym is where strength meets innovation. Our state-of-the-art facility and expert trainers will help you build the body you\'ve always dreamed of.',
    stat1: { number: '1500', suffix: '+', label: 'Trained People' },
    stat2: { number: '100', suffix: '+', label: 'Modern Equipment' },
    secondParagraph: 'Our cutting-edge equipment and personalized training programs are designed to maximize your potential.',
    imageUrl: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80'
  },
  classes: {
    subtitleSuffix: 'Services',
    title: 'Our Featured Classes',
    description: 'Discover our premium fitness programs designed to challenge your limits and unlock your true potential.',
    items: [
      { icon: '🏋️', title: 'Power Lifting', description: 'The Best Body Fitness in Town.' },
      { icon: '💪', title: 'Workout', description: 'Full-body fitness programs.' }
    ]
  },
  cta: {
    title: 'We Are Always Providing Best Fitness Service For You',
    buttonText: 'JOIN OUR TEAM →'
  },
  trainers: {
    subtitleSuffix: 'Trainers',
    title: 'Our Professional Training Staff',
    description: 'Meet our dedicated trainers for personalized fitness guidance.',
    list: [
      { name: 'Mouzim', specialty: 'Trainer', imageUrl: '/images/mouzim.JPG' },
      { name: 'Aqib', specialty: 'Trainer', imageUrl: '/images/profile.jpg' },
      { name: 'Suhail Sultan', specialty: 'Trainer', imageUrl: '/images/suhail.jpg' },
      { name: 'Shaheen Majeed', specialty: 'Trainer', imageUrl: '/images/shaheen.jpeg' },
      { name: 'Sadiya', specialty: 'Trainer', imageUrl: '/images/profile.jpg' },
      { name: 'Bilal Ahmad', specialty: 'Trainer', imageUrl: '/images/trainer.jpg' }
    ]
  },
  pricing: {
    title: 'MEMBERSHIP LEVEL',
    description: 'Choose the perfect membership plan that fits your lifestyle and fitness goals.',
    paymentInfo: '💳 Cash Payment | 📱 Online Transfer',
    categories: [
      { title: 'Membership Level (A)', plans: [{ name: 'Student Membership', badge: 'Level A' }, { name: 'Adult Membership', badge: 'Level A' }] },
      { title: 'Membership Level (B)', plans: [{ name: 'Individual Membership', badge: 'Level B', featured: true }, { name: 'Premium Membership', badge: 'Level B' }] }
    ]
  },
  becomeTrainer: {
    title: 'Become A Trainer',
    description: 'Join our elite team of fitness professionals and help others transform their lives through the power of fitness.',
    buttonText: 'JOIN OUR TEAM →'
  },
  contact: {
    heading: 'Contact Us',
    subtitle: 'Reach out to us for any inquiries about our gym, classes, or membership plans.',
    addressTitle: 'Our Location',
    addressLines: ['De Muscle Factory Gym', 'Habak Naseem Bagh', 'Near Owais karni Masjid', 'Srinagar, Jammu and Kashmir', '190006'],
    phone: '9419445026, 7006117955',
    email: 'info@demusclefactory.com',
    hours: '5:00 AM - 11:00 PM'
  }
};

// Keys that should be replaced entirely (no deep merge)
const REPLACE_KEYS = ['slots', 'items', 'list', 'addressLines', 'categories', 'descriptionLines', 'plans'];

function deepMergeForDisplay(existing, next) {
  if (next == null || typeof next !== 'object') return next !== undefined ? next : existing;
  if (Array.isArray(next)) return next;
  const out = { ...existing };
  Object.keys(next).forEach((key) => {
    if (REPLACE_KEYS.indexOf(key) !== -1) {
      out[key] = next[key] != null ? next[key] : existing[key];
    } else if (next[key] && typeof next[key] === 'object' && !Array.isArray(next[key])) {
      out[key] = deepMergeForDisplay(existing[key] || {}, next[key]);
    } else {
      out[key] = next[key];
    }
  });
  return out;
}

/** Merged site content for display/editing: defaults + tenant.siteContent */
export function getMergedSiteContent(tenant) {
  const t = tenant?.siteContent || {};
  return deepMergeForDisplay(JSON.parse(JSON.stringify(DEFAULT_SITE_CONTENT)), t);
}

// Get all tenants (super admin only)
export const getAllTenants = async () => {
  try {
    const tenantsRef = ref(db, TENANTS_ROOT);
    const snapshot = await get(tenantsRef);
    if (snapshot.exists()) {
      const tenants = [];
      snapshot.forEach((childSnapshot) => {
        tenants.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      return { success: true, tenants };
    }
    return { success: true, tenants: [] };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to load tenants'
    };
  }
};

// Get tenant by ID
export const getTenantById = async (tenantId) => {
  try {
    const tenantRef = ref(db, `${TENANTS_ROOT}/${tenantId}`);
    const snapshot = await get(tenantRef);
    if (snapshot.exists()) {
      return {
        success: true,
        tenant: { id: snapshot.key, ...snapshot.val() }
      };
    }
    return { success: false, message: 'Tenant not found' };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to load tenant'
    };
  }
};

// Normalize hostname for domain lookup (lowercase, trim, no trailing dot)
function normalizeHostname(h) {
  return (h || '').toLowerCase().trim().replace(/\.$/, '');
}

// Get tenant by custom domain (for public site when using custom domains)
export const getTenantByDomain = async (hostname) => {
  const normalized = normalizeHostname(hostname);
  if (!normalized) return { success: false, message: 'No hostname' };
  try {
    const tenantsRef = ref(db, TENANTS_ROOT);
    const snapshot = await get(tenantsRef);
    if (!snapshot.exists()) return { success: false, message: 'No tenants' };
    let found = null;
    snapshot.forEach((childSnapshot) => {
      const data = childSnapshot.val();
      if (!data || data.status !== 'active') return;
      const domains = Array.isArray(data.customDomains) ? data.customDomains : [];
      const matches = domains.some((d) => {
        const n = normalizeHostname(d);
        if (!n) return false;
        return n === normalized || n === normalized.replace(/^www\./, '') || normalized === n.replace(/^www\./, '');
      });
      if (matches) found = { id: childSnapshot.key, ...data };
    });
    if (found) return { success: true, tenant: found };
    return { success: false, message: 'No tenant for this domain' };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to lookup tenant'
    };
  }
};

// Get tenant by slug (for public site routing)
export const getTenantBySlug = async (slug) => {
  try {
    const tenantsRef = ref(db, TENANTS_ROOT);
    // Try indexed query first
    try {
      const slugQuery = query(tenantsRef, orderByChild('slug'), equalTo(slug));
      const snapshot = await get(slugQuery);
      if (snapshot.exists()) {
        let tenantId = null;
        let data = null;
        snapshot.forEach((childSnapshot) => {
          tenantId = childSnapshot.key;
          data = childSnapshot.val();
        });
        if (tenantId && data && data.status === 'active') {
          return { success: true, tenant: { id: tenantId, ...data } };
        }
      }
    } catch (_) {
      // Fallback: fetch all tenants and find by slug (e.g. if index not deployed yet)
    }
    const allSnap = await get(tenantsRef);
    if (allSnap.exists()) {
      const normalizedSlug = (slug || '').toLowerCase().trim();
      let found = null;
      allSnap.forEach((childSnapshot) => {
        const data = childSnapshot.val();
        if (data && (data.slug || '').toLowerCase() === normalizedSlug && data.status === 'active') {
          found = { id: childSnapshot.key, ...data };
        }
      });
      if (found) {
        return { success: true, tenant: found };
      }
    }
    return { success: false, message: 'Tenant not found or inactive' };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to load tenant'
    };
  }
};

// Create tenant
export const createTenant = async (tenantData) => {
  try {
    const tenantsRef = ref(db, TENANTS_ROOT);
    const newRef = push(tenantsRef);
    const tenantId = newRef.key;
    const now = new Date().toISOString();
    const tenant = {
      name: tenantData.name || 'New Tenant',
      slug: (tenantData.slug || tenantId).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      status: tenantData.status || 'active',
      createdAt: now,
      branding: {
        ...DEFAULT_TENANT_BRANDING,
        ...(tenantData.branding || {})
      },
      features: {
        ...DEFAULT_TENANT_FEATURES,
        ...(tenantData.features || {})
      },
      settings: tenantData.settings || {},
      siteContent: tenantData.siteContent ?? JSON.parse(JSON.stringify(DEFAULT_SITE_CONTENT)),
      customDomains: Array.isArray(tenantData.customDomains) ? tenantData.customDomains.map((d) => (typeof d === 'string' ? d.trim() : '')).filter(Boolean) : []
    };
    await set(newRef, tenant);
    return {
      success: true,
      tenantId,
      tenant: { id: tenantId, ...tenant },
      message: 'Tenant created successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to create tenant'
    };
  }
};

// Update tenant
export const updateTenant = async (tenantId, updates) => {
  try {
    const tenantRef = ref(db, `${TENANTS_ROOT}/${tenantId}`);
    const snapshot = await get(tenantRef);
    if (!snapshot.exists()) {
      return { success: false, message: 'Tenant not found' };
    }
    const current = snapshot.val();
    const allowed = ['name', 'slug', 'status', 'branding', 'features', 'settings', 'siteContent', 'customDomains'];
    const patch = {};
    allowed.forEach((key) => {
      if (updates[key] !== undefined) patch[key] = updates[key];
    });
    if (patch.branding && current.branding) {
      patch.branding = { ...current.branding, ...patch.branding };
    }
    if (patch.features && current.features) {
      patch.features = { ...current.features, ...patch.features };
    }
    if (patch.siteContent !== undefined) {
      const existing = current.siteContent || {};
      const next = typeof patch.siteContent === 'object' && patch.siteContent !== null ? patch.siteContent : {};
      patch.siteContent = deepMergeSiteContent(existing, next);
    }
    if (patch.customDomains !== undefined) {
      const raw = Array.isArray(patch.customDomains) ? patch.customDomains : [];
      patch.customDomains = raw.map((d) => (typeof d === 'string' ? d.trim() : '')).filter(Boolean);
    }
    await update(tenantRef, patch);
    return { success: true, message: 'Tenant updated successfully' };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to update tenant'
    };
  }
};

// Delete tenant (hard delete; use with caution)
export const deleteTenant = async (tenantId) => {
  try {
    const tenantRef = ref(db, `${TENANTS_ROOT}/${tenantId}`);
    await remove(tenantRef);
    return { success: true, message: 'Tenant deleted' };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to delete tenant'
    };
  }
};

// Suspend / activate tenant
export const setTenantStatus = async (tenantId, status) => {
  if (status !== 'active' && status !== 'suspended') {
    return { success: false, message: 'Invalid status' };
  }
  return updateTenant(tenantId, { status });
};
